import React, { useEffect, useRef, useState } from 'react';
import './adminLookbook.css';
import { useLookbook } from '../../hooks/useLookbook';
import useImageUpload from '../../hooks/useImageUpload';
import { X } from 'lucide-react';

function compressImage(file, maxSize = 5 * 1024 * 1024, maxWidth = 5000, maxHeight = 5000, initialQuality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        // Always reduce to maxWidth/maxHeight to ensure safe compression
        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }
        
        // Compress with quality reduction if needed to meet size limit
        let quality = initialQuality;
        let lastBlobSize = Infinity;
        let attempt = 0;
        const maxAttempts = 10;
        
        const attemptCompress = () => {
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas context unavailable'));
            return;
          }
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to blob with current quality
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Image compression failed'));
                return;
              }

              if (blob.size <= maxSize) {
                // Size is acceptable
                const compressedFile = new File([blob], file.name, { type: 'image/jpeg' });
                console.log(
                  `[Lookbook] Compressed: ${(file.size / (1024 * 1024)).toFixed(2)}MB → ${(blob.size / (1024 * 1024)).toFixed(2)}MB (quality: ${(quality * 100).toFixed(0)}%)`
                );
                resolve(compressedFile);
              } else if (blob.size >= lastBlobSize || quality <= 0.2) {
                // No more compression improvement or quality too low
                const compressedFile = new File([blob], file.name, { type: 'image/jpeg' });
                console.warn(`[Lookbook] Could not compress below ${(blob.size / (1024 * 1024)).toFixed(2)}MB. Fallback attempt.`);
                resolve(compressedFile);
              } else if (attempt < maxAttempts) {
                // Try again with lower quality
                lastBlobSize = blob.size;
                quality = Math.max(0.2, quality - 0.08);
                attempt++;
                attemptCompress();
              } else {
                // Maximum attempts reached
                const compressedFile = new File([blob], file.name, { type: 'image/jpeg' });
                console.warn('[Lookbook] Max compression attempts reached');
                resolve(compressedFile);
              }
            },
            'image/jpeg',
            quality
          );
        };
        
        attemptCompress();
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      
      img.src = event.target.result;
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
}

function AdminLookbook({ idToken }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [uploadedImages, setUploadedImages] = useState([]);

  const { addLookbook, loading, error } = useLookbook();

  const { handleImageUpload, uploading, error: uploadError } = useImageUpload();

  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const successTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  const handleImageSelect = async (e) => {
    const files = e.target.files;
    if (!files) return;

    for (const file of files) {
      try {
        setFormError('');
        
        // OPTIMIZATION: Compress image aggressively to ensure upload success
        // Targets 5MB max with max 5000x5000 dimensions
        let fileToUpload = file;
        if (file.type.startsWith('image/')) {
          try {
            const compressedFile = await compressImage(file, 5 * 1024 * 1024, 5000, 5000, 0.85);
            fileToUpload = compressedFile;
          } catch (compressErr) {
            // If compression fails, try uploading original
            setFormError(`Compression de "${file.name}" impossible, tentative d'upload...`);
            console.warn('Image compression failed:', compressErr);
          }
        }
        
        // Upload image to server
        const url = await handleImageUpload(fileToUpload, idToken);
        
        if (url) {
          // Add URL to uploaded images
          setUploadedImages((prev) => [...prev, url]);
          setFormSuccess(`Image "${file.name}" ajoutée avec succès`);
        } else {
          throw new Error('Upload failed - no URL returned');
        }
      } catch (err) {
        setFormError(`Erreur upload "${file.name}": ${err.message}`);
      }
    }

    // Reset file input
    e.target.value = '';
  };

  const removeImage = (indexToRemove) => {
    setUploadedImages((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // VALIDATION: Check required fields
    if (!name.trim()) {
      setFormError('Le nom du lookbook est requis');
      return;
    }

    if (uploadedImages.length === 0) {
      setFormError('Au moins une image est requise');
      return;
    }

    try {
      setFormError('');
      setFormSuccess('');

      // Prepare lookbook data
      const lookbookData = {
        name: name.trim(),
        description: description.trim(),
        images: uploadedImages,
      };

      // Create lookbook via hook
      const created = await addLookbook(lookbookData, idToken);

      if (created) {
        // Success! Reset form
        setFormSuccess('Lookbook créé avec succès!');
        setName('');
        setDescription('');
        setUploadedImages([]);
        
        // Clear success message after 3 seconds
        if (successTimeoutRef.current) {
          clearTimeout(successTimeoutRef.current);
        }
        successTimeoutRef.current = setTimeout(() => setFormSuccess(''), 3000);
      } else {
        throw new Error(error || 'Failed to create lookbook');
      }
    } catch (err) {
      setFormError(`Erreur: ${err.message}`);
    }
  };

  const handleCancel = () => {
    setName('');
    setDescription('');
    setUploadedImages([]);
    setFormError('');
    setFormSuccess('');
  };

  return (
    <div className="admin-lookbook-container">
      {/* Header section */}
      <div className="admin-lookbook-header">
        <h2>Créer un Lookbook</h2>
        <p>
          Créez un nouveau lookbook avec des images de votre collection.
        </p>
      </div>

      {/* Main form */}
      <form className="admin-lookbook-form" onSubmit={handleSubmit}>
        {/* Lookbook name input */}
        <label className="admin-lookbook-label">
          <span>Nom du lookbook *</span>
          <input
            className="admin-lookbook-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex: Automne/Hiver 2025-26"
            required
          />
        </label>

        {/* Lookbook description input */}
        <label className="admin-lookbook-label">
          <span>Description (optionnel)</span>
          <textarea
            className="admin-lookbook-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Décrivez la collection et son inspiration..."
          />
        </label>

        {/* Image upload section */}
        <div className="admin-lookbook-upload-section">
          <div className="admin-lookbook-upload-title">
            Images du Lookbook *
          </div>
          <p className="admin-lookbook-upload-help">
            Sélectionnez une ou plusieurs images pour le lookbook.
            Images acceptées: JPG, PNG, WebP
          </p>

          {/* File input - styled as button */}
          <div className="admin-lookbook-upload-input-wrapper">
            <input
              id="lookbook-image-input"
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp"
              onChange={handleImageSelect}
              disabled={uploading}
            />
            <label
              htmlFor="lookbook-image-input"
              className={`admin-lookbook-upload-button ${uploading ? 'is-disabled' : ''}`}
              aria-disabled={uploading}
            >
              {uploading ? 'Téléchargement...' : 'Ajouter les images'}
            </label>
          </div>

          {/* Upload status messages */}
          {uploadError && (
            <div className="admin-lookbook-upload-status admin-lookbook-upload-error">
              Erreur upload: {uploadError}
            </div>
          )}

          {/* Image preview panel */}
          {uploadedImages.length > 0 && (
            <div className="admin-lookbook-preview-panel">
              {uploadedImages.map((image, index) => (
                <div key={index} className="admin-lookbook-preview-item">
                  <img src={image} alt={`Aperçu ${index + 1}`} />
                  <button
                    type="button"
                    className="admin-lookbook-preview-remove"
                    onClick={() => removeImage(index)}
                    aria-label={`Supprimer image ${index + 1}`}
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="admin-lookbook-upload-counter">
            {uploadedImages.length} image{uploadedImages.length !== 1 ? 's' : ''}{' '}
            ajoutée{uploadedImages.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Form messages */}
        {formError && (
          <div className="admin-lookbook-status-message admin-lookbook-status-error">
            {formError}
          </div>
        )}

        {formSuccess && (
          <div className="admin-lookbook-status-message admin-lookbook-status-success">
            {formSuccess}
          </div>
        )}

        {/* Form action buttons */}
        <div className="admin-lookbook-actions">
          <button
            type="button"
            className="admin-lookbook-cancel"
            onClick={handleCancel}
          >
            Annuler
          </button>
          <button
            type="submit"
            className="admin-lookbook-submit"
            disabled={loading || uploadedImages.length === 0}
          >
            {loading ? 'Création...' : 'Créer le lookbook'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AdminLookbook;
